<template>
  <div :class="[$style.config, $style.wrap]">
    <WithBackdrop
      :class="[$style.backdrop, data?.fields?.tintOverlay && $style.tint]"
      :backdrop="computedBackdrop"
      :observe-intersection="false"
      fit="scale"
      eager
    >
      <AspectRatio ratio="16x9" />
    </WithBackdrop>

    <div :class="$style.container">
      <div :class="$style.content">
        <p v-if="computedPreheader" :class="$style.preHeader">
          {{ computedPreheader }}
        </p>
        <BaseHeading :class="$style.header" size="h1">
          {{ data?.fields?.title }}
        </BaseHeading>
        <MarkedParser :class="$style.copy" :md="data?.fields?.excerpt" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TypeArticleWithoutUnresolvableLinksResponse } from '~/types/contentful'

const { locale } = useI18n()

const props = defineProps<{
  data: TypeArticleWithoutUnresolvableLinksResponse
}>()

const computedBackdrop = computed(
  () => props.data?.fields?.heroBackdrop || props.data?.fields?.featuredImage,
)

const computedPreheader = computed(() => {
  const publishedDateTime = !props.data?.fields?.hidePublishedDateTime
    ? props.data?.fields?.publishedDateTime || ''
    : ''
  const formattedPublishedDateTime = formatLocalizedDate(
    publishedDateTime,
    'PP',
    locale.value,
  )
  const tags = props.data?.fields?.tags || []

  let computedPreheader = ''
  if (formattedPublishedDateTime) {
    computedPreheader += formattedPublishedDateTime
  }
  if (formattedPublishedDateTime && tags.length) {
    computedPreheader += ' — '
  }
  if (tags) {
    computedPreheader += tags
      .map(
        (tag, index) =>
          tag?.fields?.label + (index < tags.length - 1 ? ', ' : ''),
      )
      .join('')
  }
  return computedPreheader
})
</script>

<style lang="scss" module>
.config {
  --wrap-background: var(--black);
  --content-color: var(--white);
  --header-color: var(--white);
  --copy-color: var(--white);
  --tint-overlay: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0.75) 30%,
    rgba(0, 0, 0, 0) 54%
  );

  @include breakpoint-variables(
    '--article-hero-wrap-min-height',
    (
      base: 0,
      sm: 360px,
      lg: 560px,
    )
  );

  @include breakpoint-variables(
    '--article-hero-content-margin',
    (
      base: 0,
      md: 0 5em,
      lg: 0,
    )
  );
}

.wrap {
  background-color: var(--wrap-background);
  position: relative;
  min-height: var(--article-hero-wrap-min-height);

  @include media('md') {
    align-items: center;
    display: flex;
  }
}

.tint {
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--tint-overlay);
  }
}

.backdrop {
  @include media('md') {
    position: absolute !important;
    inset: 0;
  }
}

.container {
  display: flex;
  align-items: center;

  max-width: var(--container-max-width);
  width: 100%;
  margin: 0 auto;

  padding: 1.5em;

  @include media('md') {
    position: relative;
    z-index: 1;
  }
}

.content {
  color: var(--content-color);
  max-width: 850px;
  position: relative;
  padding: 0 2em;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background-color: var(--purple-light);
  }
}

.header {
  color: var(--header-color);
  margin: 0 0 to-rem(9px);

  @include media('md') {
    margin: 0 0 to-rem(19px);
    text-shadow: 0 4px 6px var(--black);
  }
}

.copy {
  color: var(--copy-color);
  text-shadow: 0 2px 4px var(--black);

  p {
    font-size: to-rem(19px);
    line-height: math.div(30, 19);
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: var(--roboto-condensed);
    margin: 0 0 12px;
    line-height: 1;
  }

  h1,
  h2 {
    @include fluid-type(26px, 42px);
  }

  h2 {
    line-height: math.div(32, 26);
  }
}

.preHeader {
  font-size: to-rem(17px);
  font-weight: 900;
  line-height: math.div(27, 17);
  margin-bottom: to-rem(3px);
}
</style>
